import React from 'react';
import { useTranslation } from 'react-i18next';
import { Badge } from '@/components/ui/badge';
import {
  AlertCircle, CheckCircle2, GalleryHorizontal, Loader2,
} from 'lucide-react';
import { mapPublishError } from '@/lib/publishing/mapPublishError';
import type { PublishPlatform, PublishUiStatus } from '@/lib/publishing/types';

interface Props {
  platform: PublishPlatform;
  status: PublishUiStatus;
  failReason: string | null;
  /** `SocialPublish.media_kind`; `'carousel'` adds the carousel icon. */
  mediaKind?: string | null;
}

const CarouselIcon = () => (
  <GalleryHorizontal
    className="h-3 w-3 shrink-0"
    aria-hidden="true"
    data-testid="publish-status-carousel-icon"
  />
);

/** Platform-specific card indicator for a clip's most recent publish attempt. */
export const PublishStatusBadge: React.FC<Props> = ({
  platform, status, failReason, mediaKind,
}) => {
  const { t } = useTranslation();
  if (status === 'none') return null;
  const carouselIcon = mediaKind === 'carousel' ? <CarouselIcon /> : null;

  if (status === 'publishing') {
    const labelKey = platform === 'youtube'
      ? 'publishing.badge.youtube.publishing'
      : platform === 'instagram'
        ? 'publishing.badge.instagram.publishing'
        : platform === 'facebook'
          ? 'publishing.badge.facebook.publishing'
          : 'publishing.badge.sending';
    return (
      <Badge data-testid="publish-status-badge" variant="secondary" className="gap-1">
        <Loader2 className="h-3 w-3 animate-spin" aria-hidden="true" />
        {carouselIcon}
        {t(labelKey)}
      </Badge>
    );
  }

  if (status === 'in_drafts') {
    if (platform !== 'tiktok') return null;
    return (
      <Badge data-testid="publish-status-badge" variant="secondary" className="gap-1">
        <CheckCircle2 className="h-3 w-3" aria-hidden="true" />
        {carouselIcon}
        {t('publishing.badge.inDrafts')}
      </Badge>
    );
  }

  if (status === 'published') {
    const publishedKey = platform === 'instagram'
      ? 'publishing.badge.instagram.published'
      : platform === 'youtube'
        ? 'publishing.badge.youtube.published'
        : platform === 'facebook'
          ? 'publishing.badge.facebook.published'
          : 'publishing.badge.published';
    return (
      <Badge data-testid="publish-status-badge" variant="secondary" className="gap-1">
        <CheckCircle2 className="h-3 w-3" aria-hidden="true" />
        {carouselIcon}
        {t(publishedKey)}
      </Badge>
    );
  }

  const label = t(
    platform === 'youtube'
      ? 'publishing.badge.youtube.failed'
      : platform === 'instagram'
        ? 'publishing.badge.instagram.failed'
        : platform === 'facebook'
          ? 'publishing.badge.facebook.failed'
          : 'publishing.badge.failed',
  );
  const reason = t(mapPublishError(failReason));

  return (
    <Badge
      data-testid="publish-status-badge"
      variant="destructive"
      className="h-auto max-w-full items-start gap-1 whitespace-normal"
      aria-label={`${label}: ${reason}`}
    >
      <AlertCircle className="mt-0.5 h-3 w-3 shrink-0" aria-hidden="true" />
      {carouselIcon}
      <span className="min-w-0 break-words leading-tight">
        {label}
        <span aria-hidden="true"> — </span>
        <span className="font-normal">{reason}</span>
      </span>
    </Badge>
  );
};

export default PublishStatusBadge;
